/**
 * Hex math utilities for axial coordinate system.
 * Uses flat-top hexagons with axial coordinates (q, r).
 *
 * Reference: https://www.redblobgames.com/grids/hexagons/
 */

// Hex size in pixels (distance from center to corner)
export const HEX_SIZE = 40;

// Hex dimensions for flat-top orientation
export const HEX_WIDTH = HEX_SIZE * 2;
export const HEX_HEIGHT = Math.sqrt(3) * HEX_SIZE;

// Hex dimensions for pointy-top orientation
export const HEX_WIDTH_POINTY = Math.sqrt(3) * HEX_SIZE;
export const HEX_HEIGHT_POINTY = HEX_SIZE * 2;

/**
 * Axial coordinate for a hex.
 */
export interface AxialCoord {
    q: number;
    r: number;
}

/**
 * Cube coordinate for a hex (used for some calculations).
 */
export interface CubeCoord {
    x: number;
    y: number;
    z: number;
}

/**
 * Pixel coordinate on the canvas.
 */
export interface PixelCoord {
    x: number;
    y: number;
}

/**
 * The 6 axial direction vectors for neighboring hexes.
 * Order: E, NE, NW, W, SW, SE
 */
export const AXIAL_DIRECTIONS: AxialCoord[] = [
    { q: 1, r: 0 },   // East
    { q: 1, r: -1 },  // Northeast
    { q: 0, r: -1 },  // Northwest
    { q: -1, r: 0 },  // West
    { q: -1, r: 1 },  // Southwest
    { q: 0, r: 1 },   // Southeast
];

/**
 * Convert axial coordinates to cube coordinates.
 */
export function axialToCube(hex: AxialCoord): CubeCoord {
    return {
        x: hex.q,
        z: hex.r,
        y: -hex.q - hex.r,
    };
}

/**
 * Convert cube coordinates to axial coordinates.
 */
export function cubeToAxial(cube: CubeCoord): AxialCoord {
    return {
        q: cube.x,
        r: cube.z,
    };
}

/**
 * Convert axial coordinates to pixel coordinates (center of hex).
 * Uses flat-top orientation.
 */
export function axialToPixel(hex: AxialCoord, size: number = HEX_SIZE): PixelCoord {
    const x = size * (3 / 2 * hex.q);
    const y = size * (Math.sqrt(3) / 2 * hex.q + Math.sqrt(3) * hex.r);
    return { x, y };
}

/**
 * Convert pixel coordinates to axial coordinates.
 * Returns fractional coordinates - use hexRound() to get integer coords.
 */
export function pixelToAxial(pixel: PixelCoord, size: number = HEX_SIZE): AxialCoord {
    const q = (2 / 3 * pixel.x) / size;
    const r = (-1 / 3 * pixel.x + Math.sqrt(3) / 3 * pixel.y) / size;
    return { q, r };
}

/**
 * Round fractional axial coordinates to the nearest hex.
 */
export function hexRound(hex: AxialCoord): AxialCoord {
    const cube = axialToCube(hex);

    let rx = Math.round(cube.x);
    let ry = Math.round(cube.y);
    let rz = Math.round(cube.z);

    const xDiff = Math.abs(rx - cube.x);
    const yDiff = Math.abs(ry - cube.y);
    const zDiff = Math.abs(rz - cube.z);

    // Adjust the component with the largest rounding error
    if (xDiff > yDiff && xDiff > zDiff) {
        rx = -ry - rz;
    } else if (yDiff > zDiff) {
        ry = -rx - rz;
    } else {
        rz = -rx - ry;
    }

    return cubeToAxial({ x: rx, y: ry, z: rz });
}

/**
 * Get the 6 neighboring hex coordinates.
 */
export function getNeighbors(hex: AxialCoord): AxialCoord[] {
    return AXIAL_DIRECTIONS.map((dir) => ({
        q: hex.q + dir.q,
        r: hex.r + dir.r,
    }));
}

/**
 * Calculate the distance between two hexes.
 */
export function hexDistance(a: AxialCoord, b: AxialCoord): number {
    const ac = axialToCube(a);
    const bc = axialToCube(b);
    return Math.max(
        Math.abs(ac.x - bc.x),
        Math.abs(ac.y - bc.y),
        Math.abs(ac.z - bc.z)
    );
}

/**
 * Check if two hexes are adjacent (distance of 1).
 */
export function areAdjacent(a: AxialCoord, b: AxialCoord): boolean {
    return hexDistance(a, b) === 1;
}

/**
 * Get the corner points of a hex for rendering.
 * Returns 6 points in order for drawing a polygon.
 */
export function getHexCorners(center: PixelCoord, size: number = HEX_SIZE): PixelCoord[] {
    const corners: PixelCoord[] = [];
    for (let i = 0; i < 6; i++) {
        // Flat-top orientation: start at 0 degrees
        const angleDeg = 60 * i;
        const angleRad = (Math.PI / 180) * angleDeg;
        corners.push({
            x: center.x + size * Math.cos(angleRad),
            y: center.y + size * Math.sin(angleRad),
        });
    }
    return corners;
}

/**
 * Create a unique string key for a hex coordinate.
 */
export function hexKey(hex: AxialCoord): string {
    return `${hex.q},${hex.r}`;
}

/**
 * Create a unique string key for a hex coordinate with layer.
 * Used for lore entries that are scoped to specific z-levels.
 */
export function loreHexKey(layerId: number, hex: AxialCoord): string {
    return `${layerId}:${hex.q},${hex.r}`;
}

/**
 * Get all hex coordinates within a given radius from a center hex.
 * Radius 1 = just the center hex, radius 2 = center + 6 neighbors, etc.
 */
export function getHexesInRadius(center: AxialCoord, radius: number): AxialCoord[] {
    const hexes: AxialCoord[] = [];
    // Radius 1 means just the center, radius 2 means center + ring 1, etc.
    const actualRadius = radius - 1;

    for (let q = -actualRadius; q <= actualRadius; q++) {
        for (let r = -actualRadius; r <= actualRadius; r++) {
            const s = -q - r;
            // Check if within hex distance
            if (Math.max(Math.abs(q), Math.abs(r), Math.abs(s)) <= actualRadius) {
                hexes.push({ q: center.q + q, r: center.r + r });
            }
        }
    }
    return hexes;
}

/**
 * Linear interpolation helper.
 */
function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
}

/**
 * Get all hex coordinates along a line from start to end.
 * Uses linear interpolation in cube coordinates.
 */
export function getHexLine(start: AxialCoord, end: AxialCoord): AxialCoord[] {
    const distance = hexDistance(start, end);
    if (distance === 0) {
        return [start];
    }

    const results: AxialCoord[] = [];
    const startCube = axialToCube(start);
    const endCube = axialToCube(end);

    // Sample N+1 points along the line
    for (let i = 0; i <= distance; i++) {
        const t = i / distance;
        // Interpolate in cube coordinates
        const x = lerp(startCube.x, endCube.x, t);
        const y = lerp(startCube.y, endCube.y, t);
        const z = lerp(startCube.z, endCube.z, t);

        // Round to nearest hex (cube round)
        let rx = Math.round(x);
        let ry = Math.round(y);
        let rz = Math.round(z);

        // Fix rounding errors to maintain x + y + z = 0
        const xDiff = Math.abs(rx - x);
        const yDiff = Math.abs(ry - y);
        const zDiff = Math.abs(rz - z);

        if (xDiff > yDiff && xDiff > zDiff) {
            rx = -ry - rz;
        } else if (yDiff > zDiff) {
            ry = -rx - rz;
        } else {
            rz = -rx - ry;
        }

        results.push(cubeToAxial({ x: rx, y: ry, z: rz }));
    }

    return results;
}

// ============================================================================
// Offset coordinate functions for flat-top hex boundary with flat-top tiles
// ============================================================================

/**
 * Convert offset coordinates (col, row) to pixel coordinates.
 * Uses odd-column offset: odd columns are shifted down by half a hex height.
 * This creates a flat-top hexagonal MAP SHAPE when used with isWithinBoundaryOffset.
 *
 * Layout (flat-top hex boundary):
 *        (-1,-2) (0,-2) (1,-2)           <- top edge (row -2)
 *     (-1,-1) (0,-1) (1,-1) (2,-1)       <- row -1
 *   (-2,0) (-1,0) (0,0) (1,0) (2,0)      <- middle row (widest)
 *     (-2,1) (-1,1) (0,1) (1,1)          <- row 1
 *        (-2,2) (-1,2) (0,2)             <- bottom edge (row 2)
 */
export function offsetToPixel(col: number, row: number, size: number = HEX_SIZE): PixelCoord {
    const x = size * 1.5 * col;
    const y = size * Math.sqrt(3) * (row + (col & 1) * 0.5);
    return { x, y };
}

/**
 * Convert pixel coordinates to offset coordinates (col, row).
 * Returns the offset coordinate of the hex containing the pixel.
 */
export function pixelToOffset(pixel: PixelCoord, size: number = HEX_SIZE): { col: number; row: number } {
    // Estimate column from x
    const colFloat = pixel.x / (size * 1.5);
    const col = Math.round(colFloat);

    // Calculate row, accounting for odd-column offset
    const offset = (col & 1) * 0.5;
    const rowFloat = pixel.y / (size * Math.sqrt(3)) - offset;
    const row = Math.round(rowFloat);

    return { col, row };
}

/**
 * Get the 6 neighboring hex coordinates in offset coordinate system (odd-q offset).
 * Neighbors depend on whether the column is odd or even.
 */
export function getNeighborsOffset(col: number, row: number): Array<{ col: number; row: number }> {
    if (col & 1) {
        // Odd column - neighbors shifted down
        return [
            { col: col - 1, row: row },
            { col: col - 1, row: row + 1 },
            { col: col, row: row - 1 },
            { col: col, row: row + 1 },
            { col: col + 1, row: row },
            { col: col + 1, row: row + 1 },
        ];
    } else {
        // Even column
        return [
            { col: col - 1, row: row - 1 },
            { col: col - 1, row: row },
            { col: col, row: row - 1 },
            { col: col, row: row + 1 },
            { col: col + 1, row: row - 1 },
            { col: col + 1, row: row },
        ];
    }
}

/**
 * Get all hex coordinates within a given radius from a center hex in offset coordinates.
 * Uses pixel-space distance calculation for accurate results.
 */
export function getHexesInRadiusOffset(centerCol: number, centerRow: number, radius: number): Array<{ col: number; row: number }> {
    const hexes: Array<{ col: number; row: number }> = [];
    const actualRadius = radius - 1; // radius 1 = just center, radius 2 = center + ring 1, etc.

    // Get center pixel position
    const centerPixel = offsetToPixel(centerCol, centerRow, 1); // normalized

    // Check all hexes in a bounding box
    for (let col = centerCol - actualRadius * 2; col <= centerCol + actualRadius * 2; col++) {
        for (let row = centerRow - actualRadius; row <= centerRow + actualRadius; row++) {
            const pixel = offsetToPixel(col, row, 1);
            // Calculate pixel distance and convert to hex distance
            const dx = pixel.x - centerPixel.x;
            const dy = pixel.y - centerPixel.y;
            // Approximate hex distance: each hex is about 1.5 units wide, sqrt(3) tall
            const hexDist = Math.sqrt((dx / 1.5) ** 2 + (dy / Math.sqrt(3)) ** 2);
            if (hexDist <= actualRadius + 0.5) {
                hexes.push({ col, row });
            }
        }
    }
    return hexes;
}

/**
 * Check if offset coordinates are within a REGULAR flat-top hexagonal boundary.
 * All 6 edges have equal length.
 *
 * For radius R:
 * - Rows range from -R to +R (vertical extent)
 * - Columns extend wider to form a regular hexagon
 * - The circumradius (center to vertex) = 2R in normalized pixel units
 */
export function isWithinBoundaryOffset(col: number, row: number, radius: number): boolean {
    // Convert to normalized pixel coordinates (HEX_SIZE = 1)
    const x = 1.5 * col;
    const y = Math.sqrt(3) * (row + (col & 1) * 0.5);

    // For a regular flat-top hex where rows span -radius to +radius,
    // the circumradius (distance from center to left/right vertex) is 2 * radius
    const C = 2 * radius;
    const sqrt3 = Math.sqrt(3);

    // Check if inside the regular flat-top hexagon
    // Constraint 1: horizontal bounds (left/right vertices at x = ±C)
    if (Math.abs(x) > C) return false;

    // Constraint 2: vertical bounds depend on x position
    // - For |x| <= C/2: y limit is sqrt(3)/2 * C (the flat top/bottom edges)
    // - For |x| > C/2: y limit decreases linearly to 0 at |x| = C (diagonal edges)
    const yLimit = Math.abs(x) <= C / 2
        ? sqrt3 / 2 * C
        : sqrt3 * (C - Math.abs(x));

    return Math.abs(y) <= yLimit;
}

/**
 * Get all hex coordinates along a line from start to end in offset coordinate space.
 * Uses pixel-space interpolation for accurate results with offset coordinates.
 */
export function getHexLineOffset(startCol: number, startRow: number, endCol: number, endRow: number): Array<{ col: number; row: number }> {
    // Convert to pixel coordinates (using normalized size = 1)
    const startPixel = offsetToPixel(startCol, startRow, 1);
    const endPixel = offsetToPixel(endCol, endRow, 1);

    // Calculate pixel distance to determine number of samples
    const dx = endPixel.x - startPixel.x;
    const dy = endPixel.y - startPixel.y;
    const pixelDistance = Math.sqrt(dx * dx + dy * dy);

    // Estimate number of hexes along the line (1.5 is approx hex width in normalized coords)
    const numSteps = Math.max(1, Math.ceil(pixelDistance / 1.0));

    const results: Array<{ col: number; row: number }> = [];
    const seen = new Set<string>();

    for (let i = 0; i <= numSteps; i++) {
        const t = numSteps === 0 ? 0 : i / numSteps;
        const px = startPixel.x + (endPixel.x - startPixel.x) * t;
        const py = startPixel.y + (endPixel.y - startPixel.y) * t;

        // Convert back to offset coordinates
        const { col, row } = pixelToOffset({ x: px, y: py }, 1);
        const key = `${col},${row}`;

        // Only add unique hexes
        if (!seen.has(key)) {
            seen.add(key);
            results.push({ col, row });
        }
    }

    return results;
}

